import React, { useCallback } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import DialogComponent from '../../component/dialog/dialog.component';
import i18n from '../../i18n';
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as EpSuccessFilledIcon } from '../../static/images/svg/ep_success-filled.svg';
import { ReactComponent as TriangleDangerIcon } from '../../static/images/svg/jam_triangle-danger-f.svg';
import './menu-library-share-success-dialog.style.scss';

export type MenuLibraryShareSuccessDialogProps = {
    /** 是否打开 */
    open: boolean;
    /** 关闭 */
    onClose: () => void;
    /** 分享的菜单名（可含 ` - DD/MM/YYYY` 更新时间后缀） */
    menuName: string;
    /** 成功说明行（已本地化） */
    lines: string[];
    /**
     * 是否至少有门店同步成功；false 时展示错误图标（隧道全失败、`menu/share` 失败等与菜单商店立刻使用结果一致）。
     * @default true
     */
    hadSuccessfulSync?: boolean;
};

/**
 * 分享菜单 — 成功确认（Figma 4029:28100）；壳层使用公共 `DialogComponent`；同步至所选门店含隧道分批校验时可切换失败图标。
 */
export function MenuLibraryShareSuccessDialog(props: MenuLibraryShareSuccessDialogProps) {
    const { open, onClose, menuName, lines, hadSuccessfulSync = true } = props;

    /** 点击遮罩不关闭；Esc 可关闭 */
    const handleDialogClose = useCallback(
        (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
            if (reason === 'backdropClick') {
                return;
            }
            onClose();
        },
        [onClose],
    );

    const subtitleText = menuName.trim() || '—';

    return (
        <DialogComponent
            className="menu-library-share-success-dialog"
            open={open}
            onClose={handleDialogClose}
            maxWidth={false}
            fullWidth
            aria-labelledby="menu-library-share-success-title"
            PaperProps={{
                className: 'menu-library-share-success-dialog__paper',
                elevation: 0,
                sx: {
                    maxWidth: '640px',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'var(--fill-neutral-100)',
                    backgroundColor: 'var(--fill-neutral-100)',
                    border: '2px solid var(--stroke-primary-300)',
                },
            }}
            sx={[
                {
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'var(--overlay-800-a25)',
                    },
                    '& .MuiDialog-container > .MuiPaper-root': {
                        background: 'var(--fill-neutral-100)',
                        backgroundColor: 'var(--fill-neutral-100)',
                        border: '2px solid var(--stroke-primary-300)',
                        boxShadow: 'none',
                    },
                    '& .MuiDialogTitle-root': {
                        backgroundColor: 'var(--fill-neutral-100)',
                        paddingTop: '16px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        paddingBottom: '0px',
                        width: '100%',
                        boxSizing: 'border-box',
                    },
                    '& .MuiDialogContent-root': {
                        paddingTop: '24px !important',
                    },
                    '& .dialog-footer': {
                        display: 'none',
                    },
                },
            ]}
            dialogContentStyle={{
                paddingTop: '24px',
                paddingLeft: '24px',
                paddingRight: '24px',
                paddingBottom: '24px',
            }}
            defaultHeaderProps={{
                titleAlign: 'left',
                isHiddenCloseIcon: true,
                onClose: () => onClose(),
                titleContent: (
                    <Box
                        className="menu-library-share-success-dialog__title-row"
                        sx={{ width: '100%', boxSizing: 'border-box' }}
                    >
                        <Box className="menu-library-share-success-dialog__title-block">
                            <Typography
                                id="menu-library-share-success-title"
                                component="h2"
                                className="menu-library-share-success-dialog__title"
                            >
                                {i18n.t('menu_library_detail_share_menu')}
                            </Typography>
                            <Typography component="p" className="menu-library-share-success-dialog__subtitle">
                                {subtitleText}
                            </Typography>
                        </Box>
                        <IconButton
                            type="button"
                            size="small"
                            onClick={onClose}
                            aria-label={i18n.t('extra_cost_text_close')}
                            className="menu-library-share-success-dialog__close"
                        >
                            <CloseIcon width={24} height={24} aria-hidden />
                        </IconButton>
                    </Box>
                ),
            }}
        >
            <Stack spacing={6} className="menu-library-share-success-dialog__main-stack">
                <Box className="menu-library-share-success-dialog__icon-wrap" aria-hidden>
                    {hadSuccessfulSync ? (
                        <EpSuccessFilledIcon className="menu-library-share-success-dialog__success-icon" />
                    ) : (
                        <TriangleDangerIcon className="menu-library-share-success-dialog__error-icon" width={80} height={80} />
                    )}
                </Box>

                <Stack spacing={6} className="menu-library-share-success-dialog__lines">
                    {lines.map((line, idx) => (
                        <Typography
                            key={`${idx}-${line.slice(0, 24)}`}
                            component="p"
                            className="menu-library-share-success-dialog__line"
                        >
                            {line}
                        </Typography>
                    ))}
                </Stack>

                <Button
                    type="button"
                    variant="contained"
                    disableElevation
                    fullWidth
                    className="menu-library-share-success-dialog__ok"
                    onClick={onClose}
                >
                    {i18n.t('menu_library_share_success_confirm')}
                </Button>
            </Stack>
        </DialogComponent>
    );
}

export default MenuLibraryShareSuccessDialog;
